import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { CustomerExperienceIcon } from '@jira/servicedesk__customer-service-common/src/CustomerExperienceIcon.tsx';
import { BrandTileIcon } from '@jira/servicedesk__customer-service-common/src/BrandTileIcon.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import type { NavCustomIconQuery } from './__generated__/NavCustomIconQuery.graphql';

const styles = cssMap({
	iconSkeleton: {
		backgroundColor: token('color.skeleton'),
		width: '24px',
		height: '24px',
		borderRadius: token('radius.small'),
	},
});

const IconSkeleton = () => <Box xcss={styles.iconSkeleton} />;

const iconFallback = () => <CustomerExperienceIcon size="small" />;

const NavCustomIconInnerWrapper = ({
	helpCenterId,
}: {
	helpCenterId: string;
}): React.JSX.Element | null => {
	const cloudId = useCloudId();
	const activationId = useActivationId();

	const { customerService } = useLazyLoadQuery<NavCustomIconQuery>(
		graphql`
			query NavCustomIconQuery($cloudId: ID!, $helpCenterAri: ID!) {
				customerService(cloudId: $cloudId) {
					brandingByEntityType(entityType: CUSTOMER_HUB, helpCenterId: $helpCenterAri) {
						__typename
						... on CustomerServiceBranding {
							...BrandTileIcon_branding @dangerously_unaliased_fixme
						}
					}
				}
			}
		`,
		{
			cloudId,
			helpCenterAri: `ari:cloud:help::help-center/${activationId}/${helpCenterId}`,
		},
		{ fetchPolicy: 'store-or-network' },
	);

	const branding = customerService?.brandingByEntityType;

	if (branding && '__typename' in branding && branding.__typename === 'CustomerServiceBranding') {
		return <BrandTileIcon brandingFragment={branding} size="small" />;
	}

	return <CustomerExperienceIcon size="small" />;
};

export const NavCustomIcon = ({ helpCenterId }: { helpCenterId: string }): React.JSX.Element => (
	<JSErrorBoundary
		id="NavCustomIcon"
		packageName="jiraNavigationAppsSidebarNav4CustomerService"
		teamName="boysenberry"
		sendToPrivacyUnsafeSplunk
		fallback={iconFallback}
	>
		<Placeholder name="nav-custom-icon" fallback={<IconSkeleton />}>
			<NavCustomIconInnerWrapper helpCenterId={helpCenterId} />
		</Placeholder>
	</JSErrorBoundary>
);
